import {
  getRoutes,
  QuestionnaireQuestions,
  removeAnswers,
  stateManagerHelper,
} from '@getpopsure/qnr-framework';
import { QuotePage } from 'components/QuotePage';
import routes from 'constants/routes';
import { interpolateData } from 'features/generalQuestionnaire/functions';
import { QuestionnaireJSON } from 'features/generalQuestionnaire/model';
import { Policy } from 'models/policies';
import { useEffect } from 'react';
import { Route, Switch } from 'react-router';
import { SignupQuestionnaire as QNRSignupQuestionnaire } from 'SignupQuestionnaire';

import { PreviewQuote } from '../generalPolicy/components/Quote/PreviewQuote';
import { PreviewReview } from '../generalPolicy/components/Review/PreviewReview';
import { GeneralReview } from '../generalPolicy/components/Review/Review';
import { AdjustPolicy } from './components/AdjustPolicy/AdjustPolicy.container';
import { PreviewAdjustPolicy } from './components/AdjustPolicy/AdjustPolicy.preview';
import { AdjustPolicyReview } from './components/AdjustPolicyReview/AdjustPolicyReview';
import { PreviewAdjustPolicyReview } from './components/AdjustPolicyReview/PreviewReview';
import { StartDate } from './components/StartDate/StartDate';
import { Success } from './components/Success/Success';
import { useGetInitialAnswers } from './hooks/useGetInitialAnswers';
import { AdjustPolicyAnswers, BaseQuestionnaire, BaseQuote } from './models';
import { useAdjustPolicyStorage } from './storage';

export type ClaimGroupIds = 'questionnaire';

type AdjustPolicyQuestionnaireProps = {
  data: QuestionnaireJSON;
  policy: Policy;
  policyQuestionnaire: BaseQuestionnaire;
  policyQuote: BaseQuote;
  onQuestionnaireAnswersChanged?: (answers: QuestionnaireQuestions) => void;
  verticalId: string;
  isPreview?: boolean;
};

export const AdjustPolicyQuestionnaire = ({
  data,
  policy,
  policyQuestionnaire,
  policyQuote,
  onQuestionnaireAnswersChanged,
  verticalId,
  isPreview = false,
}: AdjustPolicyQuestionnaireProps) => {
  const { meta, questions, rules: preRules, removeAnswersLogic = {} } = data;

  const initialAnswers = useGetInitialAnswers(meta, policyQuestionnaire || {});

  const { questionnaireAnswers, setQuestionnaireAnswers } =
    useAdjustPolicyStorage(verticalId);

  const rules = interpolateData<QuestionnaireJSON['rules']>(preRules, {
    answers: questionnaireAnswers,
    constants: {
      ...meta.constants,
    },
  });

  useEffect(() => {
    if (initialAnswers) {
      setQuestionnaireAnswers(initialAnswers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setQuestionnaireAnswers]);

  useEffect(() => {
    if (typeof onQuestionnaireAnswersChanged !== 'undefined') {
      onQuestionnaireAnswersChanged?.(questionnaireAnswers);
    }
  }, [onQuestionnaireAnswersChanged, questionnaireAnswers]);

  const onAnswer = (
    questionId: string,
    answer: unknown,
    additionalData: Partial<Record<string, unknown>> = {},
    calculatedFieldsAnswers: Record<string, number> = {}
  ) => {
    const answersToRemove = stateManagerHelper(
      removeAnswersLogic,
      questionnaire.components,
      questionnaireAnswers,
      questionnaire.rules
    ).getAnswersToRemove(questionId, answer) as (keyof AdjustPolicyAnswers)[];

    const newAnswers = {
      ...questionnaireAnswers,
      ...additionalData,
      ...calculatedFieldsAnswers,
      [questionId]: answer,
    };

    const nextState = removeAnswers(newAnswers, answersToRemove);

    setQuestionnaireAnswers(nextState);
  };

  const previewBasePath = routes.birdhouse.preview.adjustPolicy.path;
  const previewQuestionnairePath =
    routes.birdhouse.preview.adjustPolicy.questionnaire.path;

  const regularBasePath = routes.me.policies.adjustPolicy.path;
  const regularQuestionnairePath =
    routes.me.policies.adjustPolicy.questionnaire.path;

  const basePath = (isPreview ? previewBasePath : regularBasePath)
    .replace(':verticalId', verticalId)
    .replace(':policyId', policy.id);

  const questionnairePath = (
    isPreview ? previewQuestionnairePath : regularQuestionnairePath
  )
    .replace(':verticalId', verticalId)
    .replace(':policyId', policy.id);

  const questionnaire = {
    components: questions,
    routes: getRoutes(questions, basePath),
    rules,
  };

  return (
    <Switch>
      <Route path={questionnairePath}>
        <QNRSignupQuestionnaire
          questionnaireUrl=""
          questionnaireAnswers={questionnaireAnswers}
          questionnaire={questionnaire}
          onAnswer={onAnswer}
          configuration={{
            policy,
            policyQuestionnaire,
            policyQuote,
            isPreview,
            components: {
              ADJUST_POLICY: isPreview ? PreviewAdjustPolicy : AdjustPolicy,
              START_DATE: StartDate,
              ADJUST_POLICY_REVIEW: isPreview
                ? PreviewAdjustPolicyReview
                : AdjustPolicyReview,
              ADJUST_POLICY_SUCCESS: Success,
              QUOTE: isPreview ? PreviewQuote : QuotePage,
              REVIEW: isPreview ? PreviewReview : GeneralReview,
            },
            questions,
            debug: isPreview,
            questionnaireType: data.meta.type,
          }}
          basePath={basePath}
          questionId="intro"
          featureName={data.meta.insuranceType}
        />
      </Route>
    </Switch>
  );
};
